/**
 * NIST SP 800-63B password rules — TS port of the Flutter
 * `lib/utils/password_validator.dart`.
 *
 * Strong (NIST) ruleset:
 *   - 8–64 characters
 *   - at least one lowercase letter
 *   - at least one uppercase letter
 *   - at least one number
 *   - at least one special character
 *   - not a known-common password
 *
 * Used by Sign Up and Reset Password (and Change Password, for consistency).
 * Exposes `validateStrongPassword` (first failing message, or undefined) and
 * `getPasswordRequirements` (the live checklist) — mirroring the Dart API.
 */

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 64

/** Subset of common passwords to reject (case-insensitive). Mirrors Dart. */
const COMMON_PASSWORDS: readonly string[] = [
  'password',
  'password123',
  'password1',
  '12345678',
  '123456789',
  '1234567890',
  'qwerty123',
  'qwertyuiop',
  'abc123',
  'letmein',
  'welcome',
  'welcome123',
  'admin',
  'admin123',
  'root',
  'root123',
  'user',
  'user123',
  'guest',
  'test',
  'test123',
]

const LOWER_RE = /[a-z]/
const UPPER_RE = /[A-Z]/
const DIGIT_RE = /[0-9]/
// Mirrors the Dart special-character class.
const SPECIAL_RE = /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/`~;]/

function isCommon(password: string): boolean {
  return COMMON_PASSWORDS.includes(password.toLowerCase())
}

/**
 * Returns the first failing requirement's message, or `undefined` when the
 * password satisfies every strong (NIST) rule. Empty string returns the
 * "required" message (matches the Flutter validator's contract).
 */
export function validateStrongPassword(password: string): string | undefined {
  if (password.length === 0) return 'Password is required'
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return `Password must be no more than ${PASSWORD_MAX_LENGTH} characters`
  }
  if (!LOWER_RE.test(password)) return 'Password must contain at least one lowercase letter'
  if (!UPPER_RE.test(password)) return 'Password must contain at least one uppercase letter'
  if (!DIGIT_RE.test(password)) return 'Password must contain at least one number'
  if (!SPECIAL_RE.test(password)) return 'Password must contain at least one special character'
  if (isCommon(password)) {
    return 'This password is too common. Please choose a stronger password'
  }
  return undefined
}

/** True when the password satisfies every strong rule. */
export function meetsAllRequirements(password: string): boolean {
  return validateStrongPassword(password) === undefined
}

export interface PasswordRequirement {
  text: string
  isMet: boolean
}

/** The live requirements checklist for display (matches Dart order). */
export function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    { text: `At least ${PASSWORD_MIN_LENGTH} characters`, isMet: password.length >= PASSWORD_MIN_LENGTH },
    { text: 'At least one lowercase letter', isMet: LOWER_RE.test(password) },
    { text: 'At least one uppercase letter', isMet: UPPER_RE.test(password) },
    { text: 'At least one number', isMet: DIGIT_RE.test(password) },
    { text: 'At least one special character (!@#$%^&*)', isMet: SPECIAL_RE.test(password) },
    { text: 'Not a common password', isMet: !isCommon(password) },
  ]
}
